/*eslint complexity: off*/
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import clsx from 'clsx';
import { toast } from 'react-toastify';

import LocalHospitalIcon from '@material-ui/icons/LocalHospital';

import Radio from '@material-ui/core/Radio';
import Input from '@material-ui/core/Input';
import FormLabel from '@material-ui/core/FormLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import GridItem from '~/components/GridItem';
import InputDate from '~/components/Input/InputDate';
import GridContainer from '~/components/GridContainer';
import InputSelect from '~/components/Input/InputSelect';
import InputControl from '~/components/Input/InputControl';
import InputSelectAsync from '~/components/Input/InputSelectAsync';
import AppointmentScheduler from '~/components/AppointmentScheduler';

import Paper from '~/components/Paper';
import Form from '~/containers/Form';

import MedicalAppointmentsRecord from '~/records/MedicalAppointmentsRecord';
import Request from '~/providers/Request';
import isPresent from '~/utils/isPresent';
import { fetchUsers } from '~/utils/fetchUsers';
import useStyles from './styles';

const attrName = (attr) => MedicalAppointmentsRecord.hummanAttributeName(attr);

function MedicalAppointmentsItem({
  record,
  handleChange,
  onSubmit,
  loading,
  setRecord,
}) {
  const classesRoot = useStyles();
  const { t } = useTranslation();
  const isPersisted = record.isPersisted();
  const [doctorsOptions, setDoctorsOptions] = useState([]);
  const [attendantOptions, setAttendantsOptions] = useState([]);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [specialtiesOptions, setSpecialtiesiesOptions] = useState([]);

  const handleRadio = (event) => handleChange({ priority: event.target.value });

  // Especialidade e obrigatoria pra agendar.
  const handleSubmit = (saveAndContinue) => {
    const specialty = record.get('specialty');
    if (!specialty || !specialty.id) {
      toast.error('Selecione a especialidade.');
      return;
    }
    onSubmit(saveAndContinue);
  };

  const handleStatus = (event) => {
    if (!record.get('schedule_appointment_at'))
      handleChange({ status: event.target.value });
  };

  async function fetchParams() {
    const URLarray = document.location.search.replace('?', '').split('&');
    let URLObject = '';

    URLObject = URLarray.filter((key) => key.includes('user_id'))[0];
    if (isPresent(URLObject)) {
      const _userId = URLObject.split('=')[1];
      const { data } = await Request.get(`/admin/users/${_userId}`, null, {
        normalizeJSON: false,
      });
      const user = {
        id: Number(data.id),
        name: `${data.attributes.name}${
          data.attributes.email ? ` <${data.attributes.email}>` : ''
        }`,
      };
      setRecord(
        record.updateAttributes({
          status: 'new',
          priority: 'low',
          user: user,
        })
      );
    } else {
      setRecord(
        record.updateAttributes({
          status: 'new',
          priority: 'low',
        })
      );
    }
  }

  async function fetchCategories() {
    Request.get('/admin/appointment_categories', null, { normalizeJSON: true })
      .then((response) => {
        setCategoriesOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setCategoriesOptions([]);
      });
  }

  async function fetchSpecialties() {
    Request.get('/admin/specialties', null, { normalizeJSON: true })
      .then((response) => {
        setSpecialtiesiesOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setSpecialtiesiesOptions([]);
      });
  }

  async function fetchDoctors() {
    Request.get('/admin/users_doctors').then((result) => {
      const items = result.data.map((user) => {
        return {
          id: Number(user.id),
          name: `${user.name}${
            isPresent(user.specialties)
              ? ` <${user.specialties[0].name}>`
              : ' <Clínico Geral>'
          }`,
        };
      });
      const data = [
        ...items,
        { id: null, name: t('medicalAppointments.extras.no_doctor') },
      ];
      setDoctorsOptions(data);
    });
  }

  async function fetchAttendants() {
    Request.get('/admin/users_attendants').then((result) => {
      const data = [
        ...result.data,
        { id: null, name: t('medicalAppointments.extras.no_attendant') },
      ];
      setAttendantsOptions(data);
    });
  }

  useEffect(() => {
    fetchParams();
    fetchCategories();
    fetchSpecialties();
    fetchDoctors();
    fetchAttendants();
  }, [record.get('uuid')]);

  return (
    <>
      <Form onSubmit={handleSubmit} isPersisted={isPersisted}>
        <Paper
          Icon={LocalHospitalIcon}
          iconColor={isPersisted ? 'purple' : 'blue'}
          title={t('medicalAppointments.defSingular')}
          loading={loading}
        >
          <div className={clsx(classesRoot.root)}>
            <GridContainer>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  name={'user'}
                  value={record.get('user')}
                  label={attrName('user')}
                  required
                  inputComponent={InputSelectAsync}
                  onChange={({ user }) => {
                    setRecord(record.updateAttributes({ user: user }));
                  }}
                  InputProps={{
                    isClearable: true,
                    handleFetch: fetchUsers,
                    createEnabled: false,
                    error: false,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  name={'category'}
                  value={record.get('category')}
                  label={attrName('category')}
                  inputComponent={InputSelect}
                  onChange={({ category }) => {
                    setRecord(record.updateAttributes({ category: category }));
                  }}
                  InputProps={{
                    isClearable: true,
                    options: categoriesOptions,
                    outValueType: 'object',
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <InputControl
                  name={'attendant'}
                  value={record.get('attendant')}
                  label={attrName('attendant')}
                  inputComponent={InputSelect}
                  onChange={({ attendant }) => {
                    setRecord(
                      record.updateAttributes({ attendant: attendant })
                    );
                  }}
                  InputProps={{
                    isClearable: true,
                    options: attendantOptions,
                    outValueType: 'object',
                  }}
                />
              </GridItem>
            </GridContainer>

            <AppointmentScheduler record={record} setRecord={setRecord} />

            <GridContainer>
              <GridItem xs={4} md={4}>
                <FormLabel component="legend">{attrName('priority')}</FormLabel>
                <RadioGroup
                  aria-label="position"
                  name="position"
                  value={record.get('priority')}
                  onChange={handleRadio}
                >
                  <FormControlLabel
                    value="low"
                    control={<Radio color="primary" />}
                    label={t('medicalAppointments.priority.priority_low')}
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value="medium"
                    control={<Radio color="primary" />}
                    label={t('medicalAppointments.priority.priority_medium')}
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value="high"
                    control={<Radio color="primary" />}
                    label={t('medicalAppointments.priority.priority_high')}
                    labelPlacement="end"
                  />
                </RadioGroup>
              </GridItem>

              <GridItem xs={3} md={3}>
                <FormLabel component="legend">{attrName('status')}</FormLabel>
                <RadioGroup
                  aria-label="position"
                  name="position"
                  value={record.get('status')}
                  onChange={handleStatus}
                >
                  <FormControlLabel
                    value="new"
                    control={<Radio color="primary" />}
                    label={t('medicalAppointments.status.new')}
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value="pre_appointment"
                    control={<Radio color="primary" />}
                    label={t('medicalAppointments.status.pre_appointment')}
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value="wait_room"
                    control={<Radio color="primary" />}
                    label={t('medicalAppointments.status.wait_room')}
                    labelPlacement="end"
                  />
                </RadioGroup>
              </GridItem>

              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <InputControl
                  value={record.get('notes')}
                  name={'notes'}
                  label={attrName('notes')}
                  inputComponent={Input}
                  InputProps={{
                    multiline: true,
                    rows: '4',
                  }}
                  onChange={handleChange}
                />
              </GridItem>
            </GridContainer>
          </div>
        </Paper>
      </Form>
    </>
  );
}

MedicalAppointmentsItem.propTypes = {
  record: PropTypes.instanceOf(MedicalAppointmentsRecord).isRequired,
  handleChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  setRecord: PropTypes.func.isRequired,
};

MedicalAppointmentsItem.defaultProps = {
  loading: false,
};

export default React.memo(withRouter(MedicalAppointmentsItem));
